import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollText, Search, Loader2 } from 'lucide-react';
import { getAuditLogs, type AuditLog } from '../../services/auditService';

const fmtTime = (s?: string) => (s ? fmtDateTime(s) : '—');
const methodColor = (m?: string | null) =>
  m === 'DELETE' ? 'bg-red-100 dark:bg-red-500/15 text-red-700'
    : m === 'POST' ? 'bg-green-100 dark:bg-green-500/15 text-green-700'
    : m === 'PUT' || m === 'PATCH' ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700'
    : 'bg-muted text-muted-foreground';
const statusColor = (c?: number | null) =>
  !c ? 'text-muted-foreground' : c >= 500 ? 'text-red-600 dark:text-red-400' : c >= 400 ? 'text-amber-600' : 'text-green-600';

export const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAuditLogs({ page: 1, limit: 100 });
      setLogs(Array.isArray(res?.logs) ? res.logs : []);
    } catch {
      message.error('Could not load audit logs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      [l.user?.email, l.tenant?.name, l.endpoint, l.action, l.entityType]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [logs, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-foreground">Audit Log</h1>
        <p className="text-muted-foreground mt-1">Platform-wide record of state-changing actions.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2 flex-1">
            <ScrollText className="w-5 h-5" /> Activity ({filtered.length})
          </CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actor, tenant, endpoint…" className="pl-9" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No audit entries.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Time</th>
                    <th className="py-2 pr-4 font-medium">Actor</th>
                    <th className="py-2 pr-4 font-medium">Action</th>
                    <th className="py-2 pr-4 font-medium">Endpoint</th>
                    <th className="py-2 pr-4 font-medium">Tenant</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">Scope</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => {
                    const isPlatform = (l.tags || []).includes('platform');
                    return (
                      <tr key={l.id} className="border-b border-border/60">
                        <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">{fmtTime(l.timestamp)}</td>
                        <td className="py-2 pr-4 whitespace-nowrap text-foreground">{l.user?.email || '—'}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${methodColor(l.method)}`}>{l.method || l.action}</span>
                        </td>
                        <td className="py-2 pr-4 max-w-[280px] truncate text-muted-foreground" title={l.endpoint || ''}>{l.endpoint || l.entityType || '—'}</td>
                        <td className="py-2 pr-4 whitespace-nowrap text-foreground">{l.tenant?.name || '—'}</td>
                        <td className={`py-2 pr-4 whitespace-nowrap font-medium ${statusColor(l.statusCode)}`}>{l.statusCode ?? '—'}</td>
                        <td className="py-2">
                          {isPlatform ? <Badge className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">platform</Badge> : <span className="text-muted-foreground">tenant</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
